import type { CreativeBlock } from "../guidedFlow/types";
import type { InstagramAccount } from "../types";

/**
 * When Meta returns Instagram actors for the selected Facebook page(s), align form
 * creatives: use the canonical row when the selected id is in the list, default to
 * the first row when there is a page but no actor, and leave creatives alone when
 * the selected id is missing from the list (stale / other page).
 */
export function creativesWithSyncedInstagram(
  creatives: CreativeBlock[],
  accounts: InstagramAccount[]
): CreativeBlock[] {
  if (accounts.length === 0) return creatives;

  const defaultActor = accounts[0];

  return creatives.map((c) => {
    if (!c?.fbPage?.id) return c;

    const cur = String(c.instagramAccount?.id ?? "").trim();
    if (cur) {
      const row = accounts.find((ig) => ig.id === cur);
      if (!row) return c;
      if (
        c.instagramAccount?.username === row.username &&
        c.instagramAccount?.name === row.name
      ) {
        return c;
      }
      return { ...c, instagramAccount: row };
    }

    return { ...c, instagramAccount: defaultActor };
  });
}

/** Same rules as {@link creativesWithSyncedInstagram} for a single creative row. */
export function creativeWithSyncedInstagram(
  creative: CreativeBlock,
  accounts: InstagramAccount[]
): CreativeBlock {
  return creativesWithSyncedInstagram([creative], accounts)[0];
}

/**
 * After a page-scoped Instagram fetch, replace the selected account with the canonical
 * row from `accounts` when ids match. Does **not** choose a default account when none
 * is selected (used by edit surfaces where Facebook-only is valid).
 */
export function creativeWithResolvedInstagramRowOnly(
  creative: CreativeBlock,
  accounts: InstagramAccount[]
): CreativeBlock {
  const cur = String(creative.instagramAccount?.id ?? "").trim();
  if (!cur) return creative;
  const row = accounts.find((ig) => ig.id === cur);
  if (!row) return creative;
  if (
    creative.instagramAccount?.username === row.username &&
    creative.instagramAccount?.name === row.name
  ) {
    return creative;
  }
  return { ...creative, instagramAccount: row };
}

/** Same as {@link creativeWithResolvedInstagramRowOnly} for each creative row. */
export function creativesWithResolvedInstagramRowOnly(
  creatives: CreativeBlock[],
  accounts: InstagramAccount[]
): CreativeBlock[] {
  return creatives.map((c) => creativeWithResolvedInstagramRowOnly(c, accounts));
}

export function creativesInstagramSelectionEquals(
  a: CreativeBlock[],
  b: CreativeBlock[]
): boolean {
  if (a.length !== b.length) return false;
  return a.every((row, i) => {
    const x = b[i];
    return (
      (row?.instagramAccount?.id ?? "") === (x?.instagramAccount?.id ?? "") &&
      (row?.instagramAccount?.username ?? "") ===
        (x?.instagramAccount?.username ?? "")
    );
  });
}
